// load only the types and not the whole HLS.js library
import { DeviceCapabilities } from "chromecast-caf-receiver/cast.framework.system"
import Hls, { Level } from "hls.js"

import { canPlayFlacAudio, Platform, platform } from "./constants"

export const canPlayDolbyAtmos = (videoRef?: HTMLVideoElement) => {
  if (platform === Platform.Chromecast) {
    const context = cast.framework.CastReceiverContext.getInstance()
    const capabilites: Record<DeviceCapabilities, boolean> =
      context.getDeviceCapabilities()
    return capabilites?.is_dolby_atmos_supported ?? false
  }
  // check if the browser supports the Dolby Atmos audio track on a video element
  const video = videoRef ?? document.createElement("video")
  // check the docs on following pages
  // https://developer.dolby.com/platforms/html5/browser-support/
  // https://developer.dolby.com/tools-media/utilities/browser-feature-detection/
  const dolbyAtmosCodec = 'video/mp4;codecs="ec-3"'
  return (
    video.canPlayType(dolbyAtmosCodec) === "probably" ||
    video.canPlayType(dolbyAtmosCodec) === "maybe"
  )
}

// a list of the names of the HLS audio streams used in the DG infrastructure
export const AudioStreamQualities = {
  sd: "sd", // the standard quality album stream
  hd: "hd", // the high quality album stream
  stereoGood: "stereogood", // the standard quality audio stream in the video
  stereoHigh: "stereohigh", // the high quality audio stream in the video
  atmos: "atmos", // the dolby atmos audio stream
  lossless: "lossless", // the flac album stream
}

/**
 * a helper to remove the high bitrate levels from the HLS stream
 **/
export const removeIncompatibleLevels = (hls: Hls, is4K: boolean) => {
  const hasHvcCodec =
    hls.levels.findIndex((level) => level.codecs.includes("hvc1")) !== -1
  const hasAvcCodecs =
    hls.levels.findIndex((level) => level.codecs.includes("avc1")) !== -1
  hls.levels
    .map((level: Level, index) => {
      // we check the exact match, because some levels have multiple codecs, and there isn't much to do about it
      if (
        (!canPlayFlacAudio && level.audioCodec?.toLowerCase() === "flac") ||
        // maintain a single video format policy while preferring AVC codecs for predictably slower devices
        (is4K && hasHvcCodec && level.videoCodec?.includes("avc1")) ||
        (!is4K &&
          hasAvcCodecs &&
          (level.videoCodec?.includes("hvc1") ||
            level.videoCodec?.includes("hev"))) ||
        // DGAPP-6442: LG HD devices crash when playing 4K content
        (!is4K && level.height > 1080) ||
        // Some streams have UHD/H264 renditions, which are unsupported by many devices.
        (level.videoCodec?.includes("avc1") && level.height > 1080)
      )
        return index
      return null
    })
    // reverse the array so the mutations don't affect the level array indices
    .reverse()
    .map((levelIndex) => {
      // remove the unneeded levels from the list,
      // make sure we don't remove the last level, otherwise there's nothing left to play
      if (levelIndex !== null && hls.levels.length > 1) {
        hls.removeLevel(levelIndex)
      }
    })
}

/**
 * A helper to adjust the level controller to the users preferences
 * @param hls the HLS.js streaming engine for the given stream
 * @param lowDataMode boolean to enable low data mode
 * @param preferDolbyAtmos boolean to enable dolby atmos if available
 * @returns
 */
export const adjustToStreamingSettingsHlsJs = ({
  hls,
  preferDolbyAtmos,
  startAudioTrack,
}: {
  hls: Hls
  preferDolbyAtmos: boolean
  startAudioTrack?: string
}) => {
  if (startAudioTrack) {
    const startTrack = hls.allAudioTracks.find(
      (track) => track.name === startAudioTrack,
    )
    if (startTrack) {
      hls.setAudioOption(startTrack)
      return
    }
  }
  const canPlayAtmos = window.capabilities.dolbyAtmos
  const dolbyAtmosAudioTrack = hls.allAudioTracks.find(
    (track) => track.name === AudioStreamQualities.atmos,
  )
  // disabled on TV (only enabled on Web for testing purposes)
  const losslessAudioTrack = hls.allAudioTracks.find(
    (track) => track.name === AudioStreamQualities.lossless,
  )
  const bestQualityAudioTrack = hls.allAudioTracks.find(
    (track) =>
      track.name === AudioStreamQualities.stereoHigh ||
      track.name === AudioStreamQualities.hd,
  )
  const alternateQualityAudioTrack = hls.allAudioTracks.find(
    (track) =>
      track.name === AudioStreamQualities.stereoGood ||
      track.name === AudioStreamQualities.sd,
  )
  if (preferDolbyAtmos && canPlayAtmos && dolbyAtmosAudioTrack) {
    // find the id of the dolby atmos audio track
    hls.setAudioOption(dolbyAtmosAudioTrack)
  } else if (losslessAudioTrack && canPlayFlacAudio) {
    hls.setAudioOption(losslessAudioTrack)
  } else if (bestQualityAudioTrack) {
    // switch to the best audio track if there's one
    hls.setAudioOption(bestQualityAudioTrack)
  } else if (alternateQualityAudioTrack) {
    // switch to the best audio track if there's one
    hls.setAudioOption(alternateQualityAudioTrack)
  }
}
